import {useEffect, useState} from 'react';
import packageJson from '../../../package.json';

global.appVersion = packageJson.version;

// Máximo de recargas automáticas permitidas por sesión de tab
const MAX_RELOAD_ATTEMPTS = 2;
const RELOAD_COUNT_KEY = 'cache_bust_reload_count';
const RELOAD_VERSION_KEY = 'cache_bust_reload_version';

const semverGreaterThan = (versionA, versionB) => {
  const versionsA = versionA.split(/\./g);
  const versionsB = versionB.split(/\./g);

  while (versionsA.length || versionsB.length) {
    const a = Number(versionsA.shift());
    const b = Number(versionsB.shift());

    if (a === b) continue;
    else if (a < b) console.warn('Version mismatch, web downgraded?');
    return a !== b || isNaN(b);
  }

  return false;
};

const getReloadCount = (targetVersion) => {
  try {
    const storedVersion = sessionStorage.getItem(RELOAD_VERSION_KEY);
    if (storedVersion !== targetVersion) {
      // Nueva versión objetivo: resetear contador
      sessionStorage.setItem(RELOAD_VERSION_KEY, targetVersion);
      sessionStorage.setItem(RELOAD_COUNT_KEY, '0');
      return 0;
    }
    return parseInt(sessionStorage.getItem(RELOAD_COUNT_KEY) || '0', 10);
  } catch {
    return 0;
  }
};

const incrementReloadCount = () => {
  try {
    const current = parseInt(
      sessionStorage.getItem(RELOAD_COUNT_KEY) || '0',
      10,
    );
    sessionStorage.setItem(RELOAD_COUNT_KEY, String(current + 1));
  } catch {
    // sessionStorage no disponible (modo kiosco muy restrictivo), ignorar
  }
};

const unregisterServiceWorkers = async () => {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((r) => r.unregister()));
  }
};

const clearCaches = async () => {
  if (typeof caches !== 'undefined') {
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
  }
};

export const refreshCacheAndReload = async (targetVersion) => {
  const reloadCount = getReloadCount(targetVersion);

  if (reloadCount >= MAX_RELOAD_ATTEMPTS) {
    console.warn(
      `Cache bust: alcanzado el límite de ${MAX_RELOAD_ATTEMPTS} recargas para v${targetVersion}. Mostrando pantalla de recuperación.`,
    );
    return false; // Indica que no se debe recargar
  }

  console.log(
    `Cache bust: recargando (intento ${reloadCount + 1}/${MAX_RELOAD_ATTEMPTS}) para v${targetVersion}`,
  );

  incrementReloadCount();

  await unregisterServiceWorkers();
  await clearCaches();

  // Agregar querystring para forzar que el servidor no sirva index.html cacheado
  const url = new URL(window.location.href);
  url.searchParams.set('_cbv', targetVersion);
  window.location.replace(url.toString());

  return true;
};

function useCacheBuster() {
  const [loading, setLoading] = useState(true);
  const [isLatestVersion, setIsLatestVersion] = useState(false);
  const [needsManualRefresh, setNeedsManualRefresh] = useState(false);

  useEffect(() => {
    // Timestamp como querystring para evitar que el browser cachee meta.json
    const metaUrl = `./meta.json?_t=${Date.now()}`;

    fetch(metaUrl, {cache: 'no-store'})
      .then((response) => {
        if (!response.ok)
          throw new Error(`meta.json status: ${response.status}`);
        return response.json();
      })
      .then(async (meta) => {
        const shouldForceRefresh = semverGreaterThan(
          meta.version,
          global.appVersion,
        );
        if (shouldForceRefresh) {
          console.warn(
            `Nueva versión detectada: ${meta.version} > ${global.appVersion}`,
          );
          const willReload = await refreshCacheAndReload(meta.version);
          if (!willReload) {
            // Límite de recargas alcanzado → mostrar pantalla de recuperación
            setNeedsManualRefresh(true);
            setIsLatestVersion(false);
            setLoading(false);
          }
          // Si willReload es true, la página se redirige y este código no continúa
        } else {
          console.log(`Versión actual: ${global.appVersion}`);
          // Limpiar contador al confirmar que ya se está en la versión correcta
          try {
            sessionStorage.removeItem(RELOAD_COUNT_KEY);
            sessionStorage.removeItem(RELOAD_VERSION_KEY);
          } catch {
            // ignorar
          }
          setIsLatestVersion(true);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('Cache buster: error al obtener meta.json', err);
        // En caso de error, dejar pasar la app en lugar de bloquearla
        setIsLatestVersion(true);
        setLoading(false);
      });
  }, []);

  return {loading, isLatestVersion, needsManualRefresh};
}

export default useCacheBuster;
